/**
 * Lead Factory bundle, queue, and saved-view fetch helpers.
 */
import { authenticatedFetch } from "./dashboard-auth.js";
import { leadFactorySavedViewScopes } from "./dashboard-urls.js";
import {
  acquisitionPipelineUrl,
  acquisitionWorkQueueUrl,
  assetDispositionUrl,
  assetWorkUrl,
  contactQueueUrl,
  contactWorkCapacityUrl,
  contactWorkOperatorEventsUrl,
  contactWorkOperatorOutcomesUrl,
  contactWorkQueueUrl,
  contactWorkThroughputUrl,
  followUpQueueUrl,
  getAcquisitionPipelineFilters,
  getAcquisitionWorkFilters,
  getAssetDispositionFilters,
  getAssetWorkFilters,
  getContactQueueFilters,
  getContactWorkFilters,
  getLeadFactoryMetricsDays,
  getLeadInventoryFilters,
  getReviewQueueFilters,
  leadFactoryWindowUrl,
  leadInventoryUrl,
  reviewQueueUrl
} from "./dashboard-saved-view-state.js";
import {
  leadFactoryAcquisitionPipelineUrl,
  leadFactoryAcquisitionWorkQueueUrl,
  leadFactoryBusinessMetricsUrl,
  leadFactoryContactLetterPacketsUrl,
  leadFactoryContactQueueUrl,
  leadFactoryContactWorkCapacityUrl,
  leadFactoryContactWorkOperatorEventsUrl,
  leadFactoryContactWorkOperatorOutcomesUrl,
  leadFactoryContactWorkQueueUrl,
  leadFactoryContactWorkThroughputUrl,
  leadFactoryDocumentThroughputQueueUrl,
  leadFactoryDocumentThroughputUrl,
  leadFactoryFinanceVarianceQueueUrl,
  leadFactoryFollowUpQueueUrl,
  leadFactoryLeadGenerationQueueUrl,
  leadFactoryLeadGenerationWorkbenchUrl,
  leadFactoryLeadSupplyBlockersUrl,
  leadFactoryLeadsUrl,
  leadFactoryMortgageEvidenceReviewUrl,
  leadFactoryOwnedPropertyDispositionsUrl,
  leadFactoryOwnedPropertyFinanceReconciliationsUrl,
  leadFactoryOwnedPropertyFinanceRecordsUrl,
  leadFactoryOwnedPropertyTasksUrl,
  leadFactoryPdfInboxUrl,
  leadFactoryReferencedDeedRetrievalJobsUrl,
  leadFactoryReviewQueueOutcomeDecisionsUrl,
  leadFactoryReviewQueueOutcomesUrl,
  leadFactoryReviewQueueSlaUrl,
  leadFactoryReviewQueueUrl,
  leadFactorySavedViewEventsUrl,
  leadFactorySavedViewsUrl,
  leadFactoryStToDtStatusUrl,
  leadFactorySummaryUrl,
  leadFactoryTasksUrl
} from "./dashboard-urls.js";
import type {
  LeadFactoryAcquisitionPipelineResponse,
  LeadFactoryBundle,
  LeadFactoryBusinessMetrics,
  LeadFactoryContactLetterPacketsResponse,
  LeadFactoryContactWorkCapacityResponse,
  LeadFactoryContactWorkOperatorEventsResponse,
  LeadFactoryContactWorkOperatorOutcomesResponse,
  LeadFactoryContactWorkThroughputResponse,
  LeadFactoryDocumentThroughput,
  LeadFactoryDocumentThroughputQueueResponse,
  LeadFactoryDocumentThroughputQueues,
  LeadFactoryFinanceVarianceQueueResponse,
  LeadFactoryLeadGenerationQueueResponse,
  LeadFactoryLeadGenerationWorkbenchResponse,
  LeadFactoryLeadSupplyBlockersResponse,
  LeadFactoryLeadsResponse,
  LeadFactoryMortgageEvidenceReviewResponse,
  LeadFactoryOwnedPropertyDispositionsResponse,
  LeadFactoryOwnedPropertyFinanceReconciliationsResponse,
  LeadFactoryOwnedPropertyFinanceRecordsResponse,
  LeadFactoryPdfInboxResponse,
  LeadFactoryReferencedDeedRetrievalJobQueues,
  LeadFactoryReferencedDeedRetrievalJobStatus,
  LeadFactoryReferencedDeedRetrievalJobsResponse,
  LeadFactoryReviewQueueOutcomeDecisionsResponse,
  LeadFactoryReviewQueueOutcomesResponse,
  LeadFactoryReviewQueueResponse,
  LeadFactoryReviewQueueSlaResponse,
  LeadFactorySavedViewEventsResponse,
  LeadFactorySavedViewResponse,
  LeadFactorySavedViewsResponse,
  LeadFactoryStToDtStatus,
  LeadFactorySummary,
  LeadFactoryTasksResponse
} from "./dashboard-lead-factory-types.js";

type LeadFactorySavedViewScope = (typeof leadFactorySavedViewScopes)[number];

export async function fetchLeadFactory(beforeFetch: () => Promise<void>): Promise<LeadFactoryBundle> {
  await beforeFetch();
  const metricsDays = getLeadFactoryMetricsDays();
  const leadInventoryFilters = getLeadInventoryFilters();
  const reviewQueueFilters = getReviewQueueFilters();
  const contactQueueFilters = getContactQueueFilters();
  const contactWorkFilters = getContactWorkFilters();
  const acquisitionPipelineFilters = getAcquisitionPipelineFilters();
  const acquisitionWorkFilters = getAcquisitionWorkFilters();
  const assetWorkFilters = getAssetWorkFilters();
  const assetDispositionFilters = getAssetDispositionFilters();
  const [
    summary,
    businessMetrics,
    documentThroughput,
    stToDtStatus,
    leadSupplyBlockers,
    referencedDeedRetrievalJobs,
    documentThroughputQueues,
    leadGenerationWorkbench,
    mortgageEvidenceReview,
    leadGenerationQueue,
    businessMetricsSavedViews,
    leadInventorySavedViews,
    reviewQueueSavedViews,
    contactQueueSavedViews,
    contactWorkSavedViews,
    acquisitionPipelineSavedViews,
    acquisitionWorkSavedViews,
    assetWorkSavedViews,
    assetDispositionSavedViews,
    pdfInbox,
    leads,
    tasks,
    reviewQueue,
    reviewQueueSla,
    reviewQueueOutcomes,
    reviewQueueOutcomeDecisions,
    contactQueue,
    contactWorkQueue,
    contactWorkCapacity,
    contactWorkThroughput,
    contactWorkOperatorEvents,
    contactWorkOperatorOutcomes,
    followUpQueue,
    contactLetterPackets,
    acquisitionPipeline,
    acquisitionWorkQueue,
    ownedPropertyTasks,
    ownedPropertyDispositions,
    ownedPropertyFinanceRecords,
    ownedPropertyFinanceReconciliations,
    financeVarianceQueue
  ] = await Promise.all([
    fetchJson<LeadFactorySummary>(leadFactorySummaryUrl),
    fetchJson<LeadFactoryBusinessMetrics>(leadFactoryWindowUrl(leadFactoryBusinessMetricsUrl, metricsDays)),
    fetchJson<LeadFactoryDocumentThroughput>(leadFactoryDocumentThroughputUrl),
    fetchJson<LeadFactoryStToDtStatus>(leadFactoryStToDtStatusUrl),
    fetchJson<LeadFactoryLeadSupplyBlockersResponse>(leadFactoryLeadSupplyBlockersUrl),
    fetchReferencedDeedRetrievalJobQueues(),
    fetchDocumentThroughputQueues(),
    fetchJson<LeadFactoryLeadGenerationWorkbenchResponse>(leadFactoryLeadGenerationWorkbenchUrl),
    fetchJson<LeadFactoryMortgageEvidenceReviewResponse>(leadFactoryMortgageEvidenceReviewUrl),
    fetchJson<LeadFactoryLeadGenerationQueueResponse>(leadFactoryLeadGenerationQueueUrl),
    fetchSavedViews("business_metrics"),
    fetchSavedViews("lead_inventory"),
    fetchSavedViews("review_queue"),
    fetchSavedViews("contact_queue"),
    fetchSavedViews("contact_work"),
    fetchSavedViews("acquisition_pipeline"),
    fetchSavedViews("acquisition_work"),
    fetchSavedViews("asset_work"),
    fetchSavedViews("asset_disposition"),
    fetchJson<LeadFactoryPdfInboxResponse>(leadFactoryPdfInboxUrl),
    fetchJson<LeadFactoryLeadsResponse>(leadInventoryUrl(leadFactoryLeadsUrl, leadInventoryFilters)),
    fetchJson<LeadFactoryTasksResponse>(leadFactoryTasksUrl),
    fetchJson<LeadFactoryReviewQueueResponse>(reviewQueueUrl(leadFactoryReviewQueueUrl, reviewQueueFilters)),
    fetchJson<LeadFactoryReviewQueueSlaResponse>(leadFactoryReviewQueueSlaUrl),
    fetchJson<LeadFactoryReviewQueueOutcomesResponse>(leadFactoryWindowUrl(leadFactoryReviewQueueOutcomesUrl, metricsDays)),
    fetchJson<LeadFactoryReviewQueueOutcomeDecisionsResponse>(leadFactoryWindowUrl(leadFactoryReviewQueueOutcomeDecisionsUrl, metricsDays)),
    fetchJson<LeadFactoryLeadsResponse>(contactQueueUrl(leadFactoryContactQueueUrl, contactQueueFilters)),
    fetchJson<LeadFactoryTasksResponse>(contactWorkQueueUrl(leadFactoryContactWorkQueueUrl, contactWorkFilters)),
    fetchJson<LeadFactoryContactWorkCapacityResponse>(contactWorkCapacityUrl(leadFactoryContactWorkCapacityUrl, contactWorkFilters)),
    fetchJson<LeadFactoryContactWorkThroughputResponse>(contactWorkThroughputUrl(leadFactoryContactWorkThroughputUrl, contactWorkFilters)),
    fetchJson<LeadFactoryContactWorkOperatorEventsResponse>(contactWorkOperatorEventsUrl(leadFactoryContactWorkOperatorEventsUrl, contactWorkFilters)),
    fetchJson<LeadFactoryContactWorkOperatorOutcomesResponse>(contactWorkOperatorOutcomesUrl(leadFactoryContactWorkOperatorOutcomesUrl, contactWorkFilters)),
    fetchJson<LeadFactoryTasksResponse>(followUpQueueUrl(leadFactoryFollowUpQueueUrl)),
    fetchJson<LeadFactoryContactLetterPacketsResponse>(leadFactoryContactLetterPacketsUrl),
    fetchJson<LeadFactoryAcquisitionPipelineResponse>(acquisitionPipelineUrl(leadFactoryAcquisitionPipelineUrl, acquisitionPipelineFilters)),
    fetchJson<LeadFactoryTasksResponse>(acquisitionWorkQueueUrl(leadFactoryAcquisitionWorkQueueUrl, acquisitionWorkFilters)),
    fetchJson<LeadFactoryTasksResponse>(assetWorkUrl(leadFactoryOwnedPropertyTasksUrl, assetWorkFilters)),
    fetchJson<LeadFactoryOwnedPropertyDispositionsResponse>(assetDispositionUrl(leadFactoryOwnedPropertyDispositionsUrl, assetDispositionFilters)),
    fetchJson<LeadFactoryOwnedPropertyFinanceRecordsResponse>(leadFactoryOwnedPropertyFinanceRecordsUrl),
    fetchJson<LeadFactoryOwnedPropertyFinanceReconciliationsResponse>(leadFactoryOwnedPropertyFinanceReconciliationsUrl),
    fetchJson<LeadFactoryFinanceVarianceQueueResponse>(leadFactoryFinanceVarianceQueueUrl)
  ]);
  return {
    summary,
    businessMetrics,
    documentThroughput,
    stToDtStatus,
    leadSupplyBlockers,
    referencedDeedRetrievalJobs,
    documentThroughputQueues,
    leadGenerationWorkbench,
    mortgageEvidenceReview,
    leadGenerationQueue,
    savedViews: {
      businessMetrics: businessMetricsSavedViews,
      leadInventory: leadInventorySavedViews,
      reviewQueue: reviewQueueSavedViews,
      contactQueue: contactQueueSavedViews,
      contactWork: contactWorkSavedViews,
      acquisitionPipeline: acquisitionPipelineSavedViews,
      acquisitionWork: acquisitionWorkSavedViews,
      assetWork: assetWorkSavedViews,
      assetDisposition: assetDispositionSavedViews
    },
    leadInventoryFilters,
    reviewQueueFilters,
    contactQueueFilters,
    contactWorkFilters,
    acquisitionPipelineFilters,
    acquisitionWorkFilters,
    assetWorkFilters,
    assetDispositionFilters,
    pdfInbox,
    leads,
    tasks,
    reviewQueue,
    reviewQueueSla,
    reviewQueueOutcomes,
    reviewQueueOutcomeDecisions,
    contactQueue,
    contactWorkQueue,
    contactWorkCapacity,
    contactWorkThroughput,
    contactWorkOperatorEvents,
    contactWorkOperatorOutcomes,
    followUpQueue,
    contactLetterPackets,
    acquisitionPipeline,
    acquisitionWorkQueue,
    ownedPropertyTasks,
    ownedPropertyDispositions,
    ownedPropertyFinanceRecords,
    ownedPropertyFinanceReconciliations,
    financeVarianceQueue
  };
}

export async function fetchJson<T>(url: string): Promise<T> {
  const response = await authenticatedFetch(url);
  if (!response.ok) throw new Error(`Failed to load ${url}: ${response.status}`);
  return response.json() as Promise<T>;
}

export function fetchDocumentThroughputQueues(): Promise<LeadFactoryDocumentThroughputQueues> {
  return Promise.all([
    fetchJson<LeadFactoryDocumentThroughputQueueResponse>(documentThroughputQueueStageUrl("classification")),
    fetchJson<LeadFactoryDocumentThroughputQueueResponse>(documentThroughputQueueStageUrl("extraction")),
    fetchJson<LeadFactoryDocumentThroughputQueueResponse>(documentThroughputQueueStageUrl("ocr")),
    fetchJson<LeadFactoryDocumentThroughputQueueResponse>(documentThroughputQueueStageUrl("review"))
  ]).then(([classification, extraction, ocr, review]) => ({ classification, extraction, ocr, review }));
}

export function fetchReferencedDeedRetrievalJobQueues(): Promise<LeadFactoryReferencedDeedRetrievalJobQueues> {
  return Promise.all([
    fetchJson<LeadFactoryReferencedDeedRetrievalJobsResponse>(referencedDeedRetrievalJobsStatusUrl("queued")),
    fetchJson<LeadFactoryReferencedDeedRetrievalJobsResponse>(referencedDeedRetrievalJobsStatusUrl("claimed")),
    fetchJson<LeadFactoryReferencedDeedRetrievalJobsResponse>(referencedDeedRetrievalJobsStatusUrl("failed")),
    fetchJson<LeadFactoryReferencedDeedRetrievalJobsResponse>(referencedDeedRetrievalJobsStatusUrl("completed"))
  ]).then(([queued, claimed, failed, completed]) => ({ queued, claimed, failed, completed }));
}

export function documentThroughputQueueStageUrl(stage: string): string {
  const [base, query = ""] = leadFactoryDocumentThroughputQueueUrl.split("?");
  const params = new URLSearchParams(query);
  params.set("stage", stage);
  return `${base}?${params.toString()}`;
}

export function referencedDeedRetrievalJobsStatusUrl(status: LeadFactoryReferencedDeedRetrievalJobStatus): string {
  const parsed = new URL(leadFactoryReferencedDeedRetrievalJobsUrl, window.location.origin);
  parsed.searchParams.set("status", status);
  parsed.searchParams.set("limit", "8");
  return leadFactoryReferencedDeedRetrievalJobsUrl.startsWith("http://") ||
    leadFactoryReferencedDeedRetrievalJobsUrl.startsWith("https://")
    ? parsed.toString()
    : `${parsed.pathname}${parsed.search}`;
}

export function fetchSavedViews(scope: LeadFactorySavedViewScope): Promise<LeadFactorySavedViewsResponse> {
  return fetchJson<LeadFactorySavedViewsResponse>(`${leadFactorySavedViewsUrl}?scope=${encodeURIComponent(scope)}`);
}

export function fetchSavedViewById(viewId: string): Promise<LeadFactorySavedViewResponse> {
  return fetchJson<LeadFactorySavedViewResponse>(`${leadFactorySavedViewsUrl}/${encodeURIComponent(viewId)}`);
}

export function fetchSavedViewEvents(viewId: string): Promise<LeadFactorySavedViewEventsResponse> {
  return fetchJson<LeadFactorySavedViewEventsResponse>(`${leadFactorySavedViewEventsUrl}?view_id=${encodeURIComponent(viewId)}`);
}

